// apps/client/src/pages/Welcome.tsx

import {
  AccountTree,
  ArrowForward,
  Business,
  CheckCircle,
  Description,
  Group,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Fade,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '@avanzu/types';
import { AvanzuHeader } from '../../components/AvanzuHeader';
import getRoleLabel from '../../helpers/getRoleLabel';

interface WelcomeProps {
  organisationName?: string;
  invitedByName?: string;
  userRole?: string;
}

const Welcome: React.FC<WelcomeProps> = ({ organisationName, invitedByName, userRole }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentOrganisation, user, refreshUserOrganisationState } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // Get data from URL params if not passed as props
  const orgName = organisationName || searchParams.get('orgName') || currentOrganisation?.name;
  const inviterName = invitedByName || searchParams.get('inviterName');
  const role = userRole || searchParams.get('role') || currentOrganisation?.role || UserRole.MEMBER;

  useEffect(() => {
    // Refresh user state to ensure we have the latest organisation data
    refreshUserOrganisationState();
  }, [refreshUserOrganisationState]);

  const handleGetStarted = async () => {
    setIsLoading(true);
    try {
      // Navigate to the main dashboard
      navigate('/');
    } catch (error) {
      console.error('Error navigating to dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const roleColor = role === UserRole.ADMIN ? 'secondary' : 'default';

  const features = [
    {
      icon: Description,
      title: 'R&D Planning',
      description: 'Help create and manage R&D activity plans for your organisation',
    },
    {
      icon: AccountTree,
      title: 'Transaction Mapping',
      description: 'Assist with mapping Xero transactions to R&D activities',
    },
    {
      icon: Group,
      title: 'Team Collaboration',
      description: 'Work together with your team on R&D documentation and reporting',
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Fade in={true} timeout={500}>
          <Box>
            {/* Header */}
            <Box textAlign="center" mb={4}>
              <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
                <AvanzuHeader />
              </Box>

              <Typography variant="h6" color="text.secondary" gutterBottom>
                You've successfully joined the team
              </Typography>
            </Box>

            {/* Welcome Card */}
            <Paper sx={{ p: 4, mb: 4, textAlign: 'center' }}>
              <Stack direction="row" justifyContent="center" spacing={2} alignItems="center" mb={3}>
                <CheckCircle sx={{ fontSize: 48, color: 'success.main' }} />
              </Stack>

              <Typography variant="h5" gutterBottom fontWeight={600} mb={4}>
                You're now part of {orgName || 'the organisation'}
              </Typography>

              {inviterName && (
                <Typography variant="body1" color="text.secondary" mb={2}>
                  Invited by <strong>{inviterName}</strong>
                </Typography>
              )}

              <Stack direction="row" justifyContent="center" spacing={2} alignItems="center" mb={3}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                  <Business />
                </Avatar>
                <Box textAlign="left">
                  <Typography variant="body1" fontWeight={500}>
                    {orgName || 'Your Organisation'}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={getRoleLabel(role)} size="small" color={roleColor} />
                    <Typography variant="caption" color="text.secondary">
                      Your role in this organisation
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Paper>

            {/* Action Buttons */}
            <Stack spacing={2} mb={4} alignItems="center">
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                onClick={handleGetStarted}
                disabled={isLoading}
                sx={{ py: 1.5 }}
              >
                {isLoading ? 'Getting Started...' : 'Get Started'}
              </Button>
            </Stack>

            {/* Features Overview */}
            <Divider sx={{ my: 4 }} />
            <Box mb={4}>
              <Typography variant="h6" gutterBottom textAlign="center" fontWeight={600}>
                What you can do with Avanzu
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
                R&D Tax Incentive Management Made Simple
              </Typography>

              <Stack direction="row" spacing={2}>
                {features.map((feature, index) => (
                  <Card key={index} sx={{ flex: 1 }}>
                    <CardContent>
                      <Stack spacing={2} alignItems="center" textAlign="center">
                        <feature.icon sx={{ fontSize: 48, color: 'primary.main' }} />
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            {feature.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {feature.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>

            {/* Help Text */}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Need help getting started? Contact us at{' '}
                <Box
                  component="a"
                  href="mailto:support@avanzu.com"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  support@avanzu.com
                </Box>
              </Typography>
            </Box>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default Welcome;
